import React, { useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./login-form-styles.css";
import validUsers from "../../constants/valid-user";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^[a-zA-Z0-9@$!%*?&#]*$/,
      "Password can contain letters, numbers, and special characters"
    )
    .required("Password is required"),
});

const initialValues = { email: "", password: "" };

type LoginFormComponentProps = {
  onLogin: (user: { email: string; password: string }) => void;
};

const LoginFormComponent: React.FC<LoginFormComponentProps> = ({ onLogin }) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-body">
      <div className="login-form-container">
        <div className="login-header">
          <h1>eCRF Login</h1>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const userExists = validUsers.some(
              (user) =>
                user.email === values.email && user.password === values.password
            );

            if (!userExists) {
              alert("Invalid email or password.");
            } else {
              onLogin({ email: values.email, password: values.password });
            }
          }}
        >
          {({ errors, touched }) => (
            <Form className="login-form">
              <div className="login-form-field">
                <label htmlFor="email" className="login-label">Email</label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  className={`login-input-field ${errors.email && touched.email ? "input-error" : ""}`}
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="login-error-message"
                />
              </div>

              <div className="login-form-field">
                <label htmlFor="password" className="login-label">Password</label>
                <div className="password-field-container">
                  <Field
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className={`login-input-field password-input ${errors.password && touched.password ? "input-error" : ""}`}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="eye-icon">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="eye-icon">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="password"
                  component="div"
                  className="login-error-message"
                />
              </div>

              <button type="submit" className="login-submit-btn">
                Log in
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginFormComponent;